import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "swiper/css";
import { listKitchenReviews } from "../../../services/review.api";
import { REVIEW_RATINGS } from "../../../services/normalizers";

const TASTE_COLOR = Object.fromEntries(
  REVIEW_RATINGS.map(({ code, color }) => [code, color]),
);

const fetchKitchenReviews = (kitchenId) =>
  listKitchenReviews(kitchenId, { limit: 20 });

const swiperBreakpoints = {
  1400: { slidesPerView: 4, spaceBetween: 16 },
  1200: { slidesPerView: 3, spaceBetween: 14 },
  992: { slidesPerView: 2.5, spaceBetween: 12 },
  576: { slidesPerView: 1.5, spaceBetween: 10 },
};

const arrowStyle = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 30,
  height: 30,
  borderRadius: "50%",
  border: "1px solid #e5e7eb",
  backgroundColor: "#ffffff",
  color: "#111827",
  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)",
  zIndex: 20,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "box-shadow 0.15s, background-color 0.15s",
  flexShrink: 0,
};

const Reviews = ({ kitchenId }) => {
  const swiperRef = useRef(null);

  const { data: reviews = [], isPending } = useQuery({
    queryKey: ["kitchenReviews", kitchenId],
    queryFn: () => fetchKitchenReviews(kitchenId),
    enabled: !!kitchenId,
  });

  if (isPending) {
    return (
      <div className="home-content-shell" style={{ paddingTop: 16, marginBottom: 24 }}>
        <Skeleton height={24} width={120} style={{ marginBottom: 12 }} />
        <Skeleton height={160} borderRadius={12} />
      </div>
    );
  }

  if (!reviews.length) return null;

  return (
    <div className="home-content-shell" style={{ paddingTop: 16, marginBottom: 24 }}>
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ marginBottom: 12, paddingLeft: 4, paddingRight: 4 }}
      >
        <h5
          style={{
            fontWeight: 800,
            fontSize: 18,
            margin: 0,
            fontFamily: "'Inter', sans-serif",
            color: "#1a1a2e",
          }}
        >
          Reviews
        </h5>
        <span style={{ fontSize: 13, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
          Total {reviews.length}
        </span>
      </div>

      <div style={{ position: "relative" }}>
        <Swiper
          modules={[Navigation]}
          onSwiper={(s) => { swiperRef.current = s; }}
          slidesPerView={1.3}
          spaceBetween={12}
          loop={reviews.length > 4}
          speed={500}
          breakpoints={swiperBreakpoints}
          style={{ paddingBottom: 8, paddingTop: 4 }}
        >
          {reviews.map((review) => {
            const userName = review.user?.name || "Customer";
            const avatarUrl = review.user?.avatar || null;
            const initials = userName.charAt(0).toUpperCase();

            return (
              <SwiperSlide key={review._id} style={{ height: "auto" }}>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    padding: 16,
                    border: "1px solid #f1f5f9",
                    boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={userName}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "2px solid #f1f5f9",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: "#f1f5f9",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 15,
                          color: "#374151",
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>{userName}</div>
                      <span style={{
                        display: "inline-block", marginTop: 3,
                        padding: "2px 10px", borderRadius: 12,
                        background: TASTE_COLOR[review.rating] || "#e5e7eb",
                        color: "#fff", fontSize: 11, fontWeight: 700,
                      }}>
                        {review.ratingLabel || ""}
                      </span>
                    </div>
                  </div>

                  <p
                    style={{
                      fontSize: 13,
                      color: "#6b7280",
                      lineHeight: 1.55,
                      margin: 0,
                      flexGrow: 1,
                    }}
                  >
                    {review.content || ""}
                  </p>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        <button
          onClick={() => swiperRef.current?.slidePrev()}
          style={{ ...arrowStyle, left: -6 }}
          aria-label="Previous slide"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f9fafb";
            e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,23,42,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.boxShadow = "0 4px 10px rgba(15,23,42,0.12)";
          }}
        >
          <ArrowLeft size={14} strokeWidth={2.2} />
        </button>
        <button
          onClick={() => swiperRef.current?.slideNext()}
          style={{ ...arrowStyle, right: -6 }}
          aria-label="Next slide"
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f9fafb";
            e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,23,42,0.18)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.boxShadow = "0 4px 10px rgba(15,23,42,0.12)";
          }}
        >
          <ArrowRight size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
};

export default Reviews;
